/**
 * ATO deduction theme grouping (Region Pack)
 */

import type { DeductionThemeGroup } from '../../../../types/report.types';

export interface CategoryLike {
  id: string;
  name: string;
  deductible?: boolean;
}

export interface TransactionLike {
  id: string;
  category_id?: string | null;
  amount: number;
}

const THEME_PATTERNS: Array<{ theme: string; patterns: RegExp[] }> = [
  { theme: 'Gifts and donations', patterns: [/donat/i, /charit/i, /gift/i] },
  { theme: 'Cost of managing tax affairs', patterns: [/accountant/i, /tax agent/i, /tax affairs/i] },
  { theme: 'Home office', patterns: [/home office/i, /working from home/i] },
  { theme: 'Motor vehicle and travel', patterns: [/motor vehicle/i, /travel/i, /transport/i, /fuel/i, /parking/i] },
  { theme: 'Work-related expenses', patterns: [/work/i, /uniform/i, /tools/i, /equipment/i] },
];

function resolveTheme(categoryName: string): string {
  for (const { theme, patterns } of THEME_PATTERNS) {
    if (patterns.some((p) => p.test(categoryName))) {
      return theme;
    }
  }
  return 'Other deductible';
}

export function groupByDeductionTheme(
  transactions: TransactionLike[],
  categories: CategoryLike[]
): DeductionThemeGroup[] {
  const categoryMap = new Map(categories.map((c) => [c.id, c]));
  const themeMap = new Map<string, DeductionThemeGroup>();

  for (const tx of transactions) {
    if (!tx.category_id) continue;
    const category = categoryMap.get(tx.category_id);
    if (!category?.deductible) continue;

    const theme = resolveTheme(category.name);
    const amount = Math.abs(Number(tx.amount) || 0);
    if (!themeMap.has(theme)) {
      themeMap.set(theme, {
        theme,
        totalAmount: 0,
        transactionCount: 0,
        categories: [],
      });
    }
    const group = themeMap.get(theme)!;
    group.totalAmount += amount;
    group.transactionCount += 1;

    let catEntry = group.categories.find((c) => c.categoryId === category.id);
    if (!catEntry) {
      catEntry = { categoryId: category.id, categoryName: category.name, amount: 0 };
      group.categories.push(catEntry);
    }
    catEntry.amount += amount;
  }

  return Array.from(themeMap.values()).sort((a, b) => b.totalAmount - a.totalAmount);
}
